import styled, { css } from "styled-components";
import { Link } from "react-router-dom";
import {
  glassCardStyles,
  gradientTextStyles,
  Page,
  theme,
  typography,
} from "../Homepage/styles";

/** Override global a:hover underline (shards-dashboards) on card-style links. */
const cardLinkNoUnderline = css`
  &,
  &:hover,
  &:focus,
  &:active,
  &:visited {
    text-decoration: none;
    color: inherit;
  }

  &:hover *,
  &:focus *,
  &:active *,
  &:visited * {
    text-decoration: none;
  }
`;

export const EducationPage = styled(Page)`
  background: hsl(var(--hp-background));
  background-image: none;
`;

export const EducationHero = styled.section`
  position: relative;
  padding: 104px 16px 40px;
  overflow: hidden;
  text-align: center;

  @media (min-width: 768px) {
    padding: 128px 24px 48px;
  }
`;

export const EducationHeroBgGradient = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to bottom right,
    hsl(var(--hp-primary) / 0.1) 0%,
    hsl(var(--hp-background)) 50%,
    hsl(var(--hp-accent) / 0.05) 100%
  );
`;

export const EducationHeroBgRadial = styled.div`
  pointer-events: none;
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse at center,
    hsl(var(--hp-primary) / 0.15) 0%,
    transparent 70%
  );
`;

export const EducationHeroInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 768px;
  margin: 0 auto;
`;

export const EducationHeroTitle = styled.h1`
  margin: 0;
  color: ${theme.foreground};
  font-family: ${typography.fontFamily};
  font-size: 2.25rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.25;

  @media (min-width: 768px) {
    font-size: 3.75rem;
  }

  span {
    ${gradientTextStyles}
  }
`;

export const EducationFiltersSection = styled.section`
  padding: 0 16px 32px;
`;

export const EducationContentInner = styled.div`
  max-width: 1152px;
  margin: 0 auto;
`;

export const EducationFilterCard = styled.div`
  border-radius: 16px;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: hsl(var(--hp-card) / 0.8);
  backdrop-filter: blur(24px);
  border: 1px solid hsl(var(--hp-border) / 0.5);
`;

export const EducationSearchWrap = styled.div`
  position: relative;
`;

export const EducationSearchIcon = styled.span`
  position: absolute;
  top: 50%;
  left: 16px;
  color: ${theme.textMuted};
  font-size: 18px;
  pointer-events: none;
  transform: translateY(-50%);
`;

export const EducationSearchInput = styled.input`
  width: 100%;
  height: 44px;
  padding: 0 16px 0 40px;
  border: 1px solid hsl(var(--hp-border) / 0.6);
  border-radius: 8px;
  background: hsl(var(--hp-background) / 0.4);
  color: ${theme.foreground};
  font-family: inherit;
  font-size: 0.875rem;
  outline: none;

  &::placeholder {
    color: ${theme.textMuted};
  }

  &:focus {
    border-color: ${theme.primary};
  }
`;

export const EducationTopicFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

/**
 * Lovable: px-3.5 py-1.5 rounded-full text-xs font-semibold
 * (not locked to analyst h-10 — category pills are shorter)
 */
export const EducationTopicChip = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  flex-shrink: 0;
  height: auto;
  min-height: 0;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? theme.primary : "hsl(var(--hp-border) / 0.6)"};
  background: ${({ $selected }) =>
    $selected ? theme.primary : "hsl(var(--hp-background) / 0.4)"};
  color: ${({ $selected }) =>
    $selected ? theme.primaryForeground : theme.textMuted};
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: ${typography.weights.semibold};
  line-height: 1.25;
  white-space: nowrap;
  cursor: pointer;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    border-color: hsl(var(--hp-primary) / 0.4);
    color: ${({ $selected }) =>
      $selected ? theme.primaryForeground : theme.foreground};
  }
`;

export const EducationFilterRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

export const EducationAnalystFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

export const EducationAnalystButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: ${({ $isAll }) => ($isAll ? "0" : "2px")};
  border-radius: 999px;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? theme.primary : "hsl(var(--hp-border) / 0.6)"};
  background: ${({ $isAll }) =>
    $isAll ? "hsl(var(--hp-secondary) / 0.6)" : "transparent"};
  box-shadow: ${({ $selected }) =>
    $selected
      ? `0 0 0 2px ${theme.bg}, 0 0 0 4px ${theme.primary}`
      : "none"};
  opacity: ${({ $selected }) => ($selected ? 1 : 0.8)};
  cursor: pointer;
  transition:
    opacity 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    opacity: 1;
    border-color: hsl(var(--hp-primary) / 0.4);
  }

  img {
    width: 100%;
    height: 100%;
    border-radius: 999px;
    object-fit: cover;
  }
`;

export const EducationAllLabel = styled.span`
  font-size: 0.625rem;
  font-weight: ${typography.weights.semibold};
  color: ${theme.foreground};
`;

export const EducationFilterActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`;

export const EducationClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${theme.textMuted};
  font-family: inherit;
  font-size: 0.875rem;
  cursor: pointer;
  transition: color 0.2s ease, background 0.2s ease;

  .material-icons {
    font-size: 16px;
  }

  &:hover {
    color: ${theme.foreground};
    background: ${theme.secondary};
  }
`;

export const EducationFeedSection = styled.section`
  padding: 0 16px 80px;
`;

export const EducationGrid = styled.div`
  display: grid;
  gap: 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    & > *:last-child:nth-child(odd) {
      grid-column: 1 / -1;
      width: 100%;
      max-width: calc((100% - 24px) / 2);
      justify-self: center;
    }
  }
`;

export const EducationEmptyState = styled.div`
  ${glassCardStyles}
  padding: 48px 24px;
  border-radius: 16px;
  text-align: center;
  color: ${theme.textMuted};
  font-size: 0.9375rem;

  p {
    margin: 0 0 16px;
  }
`;

export const EducationPaginationWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 40px;
`;

export const EducationCardRoot = styled.article`
  position: relative;
  ${glassCardStyles}
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 24px;
  border-radius: 16px;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: hsl(var(--hp-primary) / 0.4);
  }
`;

export const EducationCardOverlayLink = styled(Link)`
  position: absolute;
  inset: 0;
  z-index: 2;
  border-radius: 16px;
  ${cardLinkNoUnderline}

  span {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;

export const EducationCardAuthorRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

export const EducationAuthorLink = styled(Link)`
  position: relative;
  z-index: 3;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  ${cardLinkNoUnderline}

  img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    box-shadow: 0 0 0 2px hsl(var(--hp-primary) / 0.3);
  }

  strong {
    display: block;
    color: ${theme.foreground};
    font-size: 0.875rem;
    font-weight: ${typography.weights.semibold};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    display: block;
    color: ${theme.textMuted};
    font-size: 0.75rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover strong {
    color: ${theme.primary};
  }
`;

export const EducationTypeBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
  background: hsl(var(--hp-primary) / 0.1);
  color: ${theme.primary};
  font-size: 0.75rem;
  font-weight: ${typography.weights.semibold};

  .material-icons {
    font-size: 14px;
  }
`;

export const EducationCategoryBadge = styled.span`
  align-self: flex-start;
  padding: 4px 10px;
  border-radius: 999px;
  background: hsl(var(--hp-secondary) / 0.7);
  color: hsl(var(--hp-foreground) / 0.8);
  font-size: 0.625rem;
  font-weight: ${typography.weights.extrabold};
  letter-spacing: 0.15em;
  text-transform: uppercase;
`;

export const EducationCategoryBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
`;

export const EducationCardTitle = styled.h3`
  margin: 0 0 8px;
  color: ${theme.foreground};
  font-size: 1.125rem;
  font-weight: ${typography.weights.bold};
  line-height: 1.35;
  transition: color 0.2s ease;

  ${EducationCardRoot}:hover & {
    color: ${theme.primary};
  }
`;

export const EducationCardExcerpt = styled.p`
  margin: 0 0 16px;
  color: ${theme.textMuted};
  font-size: 0.875rem;
  line-height: 1.65;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const EducationTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 16px;
`;

export const EducationTag = styled.span`
  padding: 2px 8px;
  border-radius: 999px;
  background: hsl(var(--hp-secondary) / 0.6);
  color: ${theme.textMuted};
  font-size: 0.6875rem;
`;

export const EducationMeta = styled.div`
  display: flex;
  align-items: center;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid hsl(var(--hp-border) / 0.4);
  color: ${theme.textMuted};
  font-size: 0.75rem;
`;

export const EducationMetaDate = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;

  .material-icons {
    font-size: 14px;
  }
`;

export const FeaturedContributorBadge = styled.div`
  display: inline-flex;
  align-items: center;
  margin-top: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: hsl(var(--hp-primary) / 0.15);
  color: ${theme.primary};
  font-size: 0.625rem;
  font-weight: ${typography.weights.bold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  line-height: 1.3;
  white-space: nowrap;
`;

export const SkeletonGrid = styled.div`
  display: grid;
  gap: 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const SkeletonCard = styled.div`
  height: 280px;
  border-radius: 16px;
  background: hsl(var(--hp-muted) / 0.5);
  animation: education-pulse 1.5s ease-in-out infinite;

  @keyframes education-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;
